import { useState, useRef, useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import { syncUnitEpc, getUnitEpcStatus } from 'services/unitsService'

const TERMINAL = ['synced', 'not_found', 'failed']
const POLL_MS = 5000
const STOP_AFTER_MS = 95000

const readEpc = source => ({
  epcSyncStatus: source?.epcSyncStatus ?? source?.epc_sync_status ?? null,
  epcCertificate: source?.epcCertificate ?? source?.epc_certificate ?? null,
  epcLastCheckedAt: source?.epcLastCheckedAt ?? source?.epc_last_checked_at ?? null,
})

export const useEpcSync = ({ bpId, unitId, unit }) => {
  const [override, setOverride] = useState(null)
  const [checking, setChecking] = useState(false)
  const pollRef = useRef(null)
  const stopRef = useRef(null)

  const clearTimers = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (stopRef.current) clearTimeout(stopRef.current)
    pollRef.current = null
    stopRef.current = null
  }, [])

  useEffect(() => {
    setOverride(null)
    setChecking(false)
    clearTimers()
  }, [unitId, clearTimers])

  useEffect(() => () => clearTimers(), [clearTimers])

  const check = useCallback(async () => {
    if (checking || !bpId || !unitId) return
    setChecking(true)
    try {
      const res = await syncUnitEpc({ bpId, unitId })
      setOverride(readEpc(res?.data?.data))

      clearTimers()
      pollRef.current = setInterval(async () => {
        try {
          const status = await getUnitEpcStatus({ bpId, unitId })
          const next = readEpc(status?.data?.data)
          setOverride(next)
          if (TERMINAL.includes(next.epcSyncStatus)) {
            clearTimers()
            setChecking(false)
          }
        } catch {
          // transient — keep polling until the stop timer fires
        }
      }, POLL_MS)

      stopRef.current = setTimeout(() => {
        clearTimers()
        setChecking(false)
      }, STOP_AFTER_MS)
    } catch (error) {
      setChecking(false)
      toast.error(error?.response?.data?.message || 'Could not check the energy certificate register. Please try again.')
    }
  }, [bpId, unitId, checking, clearTimers])

  const epc = override || readEpc(unit)

  return { epc, checking, check }
}
